/**
 * Unit of Measure (UOM) mapping helpers
 * Maps between frontend form units and backend API enum values (UnitSymbol)
 *
 * Backend enum reference:
 * - GALLON = 'gallon'
 * - FLUID_OUNCE = 'fl oz'
 * - ACRE_INCH = 'acre-inch'
 * - SQUARE_METER = 'square meter'
 * etc.
 */

// Frontend to Backend UOM mapping
// Maps form values (with various formats) to exact backend UnitSymbol enum values
const FRONTEND_TO_BACKEND_UOM: Record<string, string> = {
  // Volume units
  gal: 'gallon',
  gallon: 'gallon',
  oz: 'fl oz',
  'fl oz': 'fl oz',
  'fluid ounce': 'fl oz',
  qt: 'quart',
  quart: 'quart',
  liter: 'liter',
  liters: 'liter',
  milliliter: 'ml', // Backend expects 'ml', not 'milliliter'
  bu: 'bushel',
  bushel: 'bushel',
  pt: 'pint',
  pint: 'pint',
  'ac-in': 'acre-inch',
  'ac in': 'acre-inch',
  'acre inch': 'acre-inch',
  'acre-inch': 'acre-inch',
  acreinch: 'acre-inch',
  cl: 'centiliter',
  centiliter: 'centiliter',
  'cu ft': 'cu ft',
  'cubic foot': 'cu ft',
  CCF: 'ccf',

  // Mass units
  'short ton': 'short ton',
  ton: 'ton',
  'metric ton': 'metric ton',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  ounce: 'ounce',
  kg: 'kg',
  kilogram: 'kg',
  mg: 'mg',
  milligram: 'mg',
  g: 'g',
  gram: 'g',
  cwt: 'cwt',

  // Area units
  'sq m': 'square meter',
  'square meter': 'square meter',
  ac: 'acre',
  acre: 'acre',
  ha: 'hectare',
  hectare: 'hectare',

  // Length units
  in: 'inch',
  inch: 'inch',

  // Time units
  hr: 'hour',
  hour: 'hour',
  day: 'day',
  min: 'minute',
  minute: 'minute',

  // Other units
  bag: 'bag',
  seed: 'seed',
  unit: 'unit',
  pkg: 'package',
  package: 'package',
  each: 'each',
  kernel: 'kernel'
};

// Backend to Frontend UOM mapping
// Maps backend UnitSymbol enum values to dropdown option keys
// Note: Dropdown options are generated by unitAbbreviation() which creates
// { key: 'acre inch', value: 'ac in' } pairs. We need to return the 'key' value.
const BACKEND_TO_FRONTEND_UOM: Record<string, string> = {
  // Volume units - backend enum values to dropdown keys
  gallon: 'gallon',
  'fl oz': 'fluid ounce', // Backend 'fl oz' → dropdown key 'fluid ounce'
  quart: 'quart',
  liter: 'liter',
  ml: 'milliliter', // Backend 'ml' → dropdown key 'milliliter'
  bushel: 'bushel',
  pint: 'pint',
  'acre-inch': 'acreinch', // Backend 'acre-inch' → dropdown key 'acreinch'
  centiliter: 'centiliter',
  'cu ft': 'cubic foot', // Backend 'cu ft' → dropdown key 'cubic foot'
  ccf: 'Ccf',

  // Mass units
  'short ton': 'short ton',
  ton: 'ton',
  'metric ton': 'metric ton',
  lb: 'pound', // Backend 'lb' → dropdown key 'pound'
  ounce: 'ounce',
  kg: 'kilogram', // Backend 'kg' → dropdown key 'kilogram'
  mg: 'milligram', // Backend 'mg' → dropdown key 'milligram'
  g: 'gram', // Backend 'g' → dropdown key 'gram'
  cwt: 'cwt',

  // Area units
  'square meter': 'square meter',
  acre: 'acre',
  hectare: 'hectare',

  // Length units
  inch: 'inch',

  // Time units
  hour: 'hour',
  day: 'day',
  minute: 'minute',

  // Other units
  bag: 'bag',
  seed: 'seed',
  unit: 'unit',
  package: 'package',
  each: 'each',
  kernel: 'kernel'
};

/**
 * Converts a frontend UOM value to backend API format
 * @param frontendUom - The frontend unit of measure (e.g., 'gal', 'oz')
 * @returns The backend unit format (e.g., 'gallon', 'fl oz') or the original value if no mapping exists
 */
export const convertFrontendToBackendUOM = (
  frontendUom: string | undefined
): string | undefined => {
  if (!frontendUom) return undefined;
  return FRONTEND_TO_BACKEND_UOM[frontendUom] || frontendUom;
};

/**
 * Converts a backend UOM value to frontend format
 * @param backendUom - The backend unit of measure (e.g., 'gallon', 'fl oz')
 * @returns The frontend unit format (e.g., 'gal', 'oz') or the original value if no mapping exists
 */
export const convertBackendToFrontendUOM = (
  backendUom: string | undefined
): string | undefined => {
  if (!backendUom) return undefined;
  return BACKEND_TO_FRONTEND_UOM[backendUom] || backendUom;
};
